import { CalculatorStore, useCalculatorStore } from '@/store/calculatorStore'
import React from 'react'
import client from '@/utils/api-client'
import { calculateResultsFromState } from './useCalculations'
import * as Sentry from '@sentry/react'
import useDebounce from './useDebounce'

export const DEBOUNCE_DELAY_MS = 5000

export function saveState(
  state: CalculatorStore,
  calculatedDiplomas: Record<string, unknown>
) {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'POST /anon-api/users/calculator',
    },
    async (sentrySpan) => {
      const { logger } = Sentry

      sentrySpan.setAttribute('uuid', state.uuid)
      sentrySpan.setAttribute('diploma_type', state.inputDiploma)
      sentrySpan.setAttribute(
        'num_courses',
        state.courses.filter((c) => c.included).length
      )
      sentrySpan.setAttribute('has_email', !!state.userDetails.email)
      sentrySpan.setAttribute('app_version', import.meta.env.VITE_APP_VERSION)

      try {
        const result = await client('/anon-api/users/calculator', {
          data: {
            calculatorState: state,
            phone: state.userDetails.phone,
            userEmail: state.userDetails.email,
            uuid: state.uuid,
            message: state.userDetails.message,
            version: import.meta.env.VITE_APP_VERSION,
            lastPath: '/',
            userAdmin: false,
            calculatedDiplomas: calculatedDiplomas,
          },
        })

        sentrySpan.setAttribute('success', true)
        logger.info('Calculator state saved successfully', {
          uuid: state.uuid,
          numCourses: state.courses.filter((c) => c.included).length,
        })

        return result
      } catch (error) {
        sentrySpan.setAttribute('success', false)
        Sentry.captureException(error, {
          tags: {
            component: 'SaveState',
            action: 'saveCalculatorState',
          },
          extra: {
            uuid: state.uuid,
            diplomaType: state.inputDiploma,
            numCourses: state.courses.filter((c) => c.included).length,
            hasUserData: !!(state.userDetails.email || state.userDetails.phone),
          },
        })

        logger.error('Failed to save calculator state', {
          uuid: state.uuid,
          error: (error as Error).message,
        })

        throw error
      }
    }
  )
}

function useSaveState() {
  const setStatus = useCalculatorStore((state) => state.setStatus)
  const setUuid = useCalculatorStore((state) => state.setUuid)
  const state = useCalculatorStore((state) => state)

  setUuid()

  // Only debounce the state - calculate results from debounced state to ensure consistency
  const debouncedState = useDebounce(state, DEBOUNCE_DELAY_MS)

  React.useEffect(() => {
    // debouncedState.status becomes 'modified' when user makes changes to calculator state
    // After debounce delay (5s), this triggers the auto-save operation
    if (debouncedState.status !== 'modified') {
      return
    }

    // Calculate results from the debounced state to ensure consistency
    const calculatedDiplomas = calculateResultsFromState(debouncedState)

    saveState(debouncedState, calculatedDiplomas)
      .then(() => {
        setStatus('saved')
      })
      .catch((error) => {
        Sentry.captureException(error, {
          tags: {
            component: 'UseSaveState',
            action: 'autoSave',
          },
          extra: {
            uuid: debouncedState.uuid,
            status: debouncedState.status,
          },
        })
      })
  }, [debouncedState, setStatus])

  React.useEffect(() => {
    if (state.status !== 'saved') {
      return
    }

    const params = new URLSearchParams(window.location.search)
    params.set('uuid', state.uuid)
    window.history.replaceState(null, '', `?${params.toString()}`)
  }, [state.status, state.uuid])
}

export default useSaveState
